import { useState } from "react";
import IntranetLayout from "@/components/IntranetLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertTriangle, Wrench, Shield, Users, Ship,
  Download, ChevronDown, ChevronUp, Search,
  BookOpen, Target, Clock, CheckCircle2, FileText, Filter, X
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { LessonCommentThread } from "@/components/LessonCommentThread";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";

type Priority = "CRITICAL" | "HIGH" | "LOW";
type Category = "spare-parts" | "maintenance" | "planned-maintenance" | "operational" | "crew" | "design";

const EXECUTIVE_IDS = [
  "bb385f23-ed54-4a65-ba02-8b80b567bf62",
  "af9054fe-4735-4981-85d8-1de34c32da12",
  "528b9027-60d4-41f9-bffc-283fa61e4efb",
];

const CATEGORIES: { key: Category; label: string; icon: typeof Wrench; accent: string }[] = [
  { key: "spare-parts", label: "Spare Parts & Logistics", icon: Wrench, accent: "#E67E22" },
  { key: "maintenance", label: "Maintenance Organization", icon: Users, accent: "#2980B9" },
  { key: "planned-maintenance", label: "Planned Maintenance Periods", icon: Clock, accent: "#27AE60" },
  { key: "operational", label: "Operational Readiness", icon: Target, accent: "#8E44AD" },
  { key: "crew", label: "Crew & Training", icon: Shield, accent: "#16A085" },
  { key: "design", label: "Design Lessons", icon: Ship, accent: "#C0392B" },
];

const priorityBadge: Record<Priority, { label: string; cls: string }> = {
  CRITICAL: { label: "Critical", cls: "bg-red-100 text-red-800 border-red-200" },
  HIGH: { label: "High", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  LOW: { label: "Moderate", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

const statusBadge: Record<string, { label: string; dot: string }> = {
  open: { label: "Open", dot: "bg-slate-400" },
  "in-progress": { label: "In Progress", dot: "bg-blue-500" },
  closed: { label: "Closed", dot: "bg-emerald-500" },
};

const EmployeeLessonsLearned = () => {
  const { user, fullName, isAdmin } = useAuth();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [priorityFilter, setPriorityFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [closeDialog, setCloseDialog] = useState<string | null>(null);
  const [closureNotes, setClosureNotes] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const canManage = isAdmin || (user?.id && EXECUTIVE_IDS.includes(user.id));

  const { data: lessons = [], isLoading } = useQuery({
    queryKey: ["lessons-learned"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("lessons_learned" as any)
        .select("*")
        .order("id");
      if (error) throw error;
      return data as any[];
    },
  });

  const closeoutMutation = useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes: string }) => {
      const { error } = await supabase
        .from("lessons_learned" as any)
        .update({
          status: "closed",
          closed_by: user!.id,
          closed_by_name: fullName,
          closed_at: new Date().toISOString(),
          closure_notes: notes,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id);
      if (error) throw error;

      const { data: companies } = await supabase.from("companies").select("id, name");
      if (companies) {
        for (const company of companies) {
          await supabase.from("notifications").insert({
            company_id: company.id,
            type: "project_update",
            title: `Lessons Learned Item Closed: ${id}`,
            message: `${fullName || "Velocity Offshore"} has closed out corrective action ${id}. ${notes ? `Notes: ${notes}` : ""}`,
          });
        }
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["lessons-learned"] });
      toast({ title: "Item Closed", description: "Lesson learned item has been closed and clients notified." });
      setCloseDialog(null);
      setClosureNotes("");
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to close item.", variant: "destructive" });
    },
  });

  const reopenMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("lessons_learned" as any)
        .update({
          status: "in-progress",
          closed_by: null,
          closed_by_name: null,
          closed_at: null,
          closure_notes: null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["lessons-learned"] });
      toast({ title: "Item Reopened" });
    },
  });

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const filtered = lessons.filter((l: any) => {
    if (priorityFilter !== "all" && l.priority !== priorityFilter) return false;
    if (statusFilter !== "all" && l.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return l.id.toLowerCase().includes(q) || l.title.toLowerCase().includes(q) || l.root_cause?.toLowerCase().includes(q) || l.corrective_action?.toLowerCase().includes(q);
    }
    return true;
  });

  const totalCritical = lessons.filter((l: any) => l.priority === "CRITICAL").length;
  const totalHigh = lessons.filter((l: any) => l.priority === "HIGH").length;
  const totalModerate = lessons.filter((l: any) => l.priority === "LOW").length;
  const closedCount = lessons.filter((l: any) => l.status === "closed").length;
  const inProgressCount = lessons.filter((l: any) => l.status === "in-progress").length;
  const openCount = lessons.filter((l: any) => l.status === "open").length;
  const completionRate = lessons.length ? Math.round((closedCount / lessons.length) * 100) : 0;

  if (isLoading) {
    return (
      <IntranetLayout>
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-teal/30 border-t-teal rounded-full animate-spin" />
        </div>
      </IntranetLayout>
    );
  }

  return (
    <IntranetLayout>
      <div className="max-w-5xl mx-auto space-y-8">

        {/* ── Page Header ── */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <p className="text-[10px] font-bold tracking-[3px] uppercase text-muted-foreground mb-2">
              LL-AIRCAT35-001 Rev. 0 · Internal
            </p>
            <h1 className="text-3xl font-display text-foreground leading-tight">
              Lessons Learned
            </h1>
            <p className="text-sm text-muted-foreground font-body mt-1">
              AIRCAT 35 Crewliner Operations — Angola insights applied to Guyana
              {canManage && <span className="ml-2 text-teal font-medium">· You can close out items</span>}
            </p>
          </div>
          <a
            href="/documents/EMGL003-Final_Lessons_Learned_AIRCAT35_Guyana.docx"
            download
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-teal text-white text-sm font-semibold hover:bg-teal/90 transition-colors shadow-sm"
          >
            <Download className="w-4 h-4" /> Download Full Report
          </a>
        </div>

        {/* ── Executive Summary ── */}
        <div className="rounded-xl border border-teal/15 bg-gradient-to-r from-teal/5 to-transparent p-6">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-teal/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <FileText className="w-4 h-4 text-teal" />
            </div>
            <div>
              <h2 className="font-semibold text-foreground mb-2">Executive Summary</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                This document captures operational lessons learned from the AIRCAT 35 Crewliner fleet deployment in Angola (TotalEnergies) and applies them to planned crew transfer operations for ExxonMobil Worldwide's Stabroek Block FPSOs. The three AIRCAT 35 Crewliners have been performing daily 250+ nm round-trip crew transfers since late 2024, providing a first-of-type operational dataset for SES technology.
              </p>
            </div>
          </div>
        </div>

        {/* ── KPI Strip ── */}
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {[
            { value: lessons.length, label: "Total Lessons", color: "var(--teal, #0D9488)" },
            { value: totalCritical, label: "Critical", color: "#DC2626" },
            { value: totalHigh, label: "High Priority", color: "#D97706" },
            { value: totalModerate, label: "Moderate", color: "#7C3AED" },
            { value: inProgressCount, label: "In Progress", color: "#2563EB" },
            { value: `${completionRate}%`, label: "Closed", color: "#059669" },
          ].map((kpi, i) => (
            <div key={i} className="rounded-xl border border-border bg-card py-4 text-center">
              <p className="text-2xl font-bold" style={{ color: kpi.color }}>{kpi.value}</p>
              <p className="text-[9px] font-semibold tracking-[1.5px] uppercase text-muted-foreground mt-1">{kpi.label}</p>
            </div>
          ))}
        </div>

        {/* ── Progress Bar ── */}
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-foreground">Implementation Progress</h3>
            <span className="text-xs text-muted-foreground">{closedCount} of {lessons.length} corrective actions closed</span>
          </div>
          <div className="w-full h-2.5 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${completionRate}%`, background: "linear-gradient(90deg, #059669, #10B981)" }}
            />
          </div>
          <div className="flex items-center gap-6 mt-3">
            <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-slate-400" /><span className="text-xs text-muted-foreground">Open {openCount}</span></div>
            <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-blue-500" /><span className="text-xs text-muted-foreground">In Progress {inProgressCount}</span></div>
            <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-emerald-500" /><span className="text-xs text-muted-foreground">Closed {closedCount}</span></div>
          </div>
        </div>

        {/* ── Search & Filter ── */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search by ID, title, root cause, or corrective action..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-10" />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`inline-flex items-center gap-1.5 px-3 h-10 rounded-lg border text-xs font-medium transition-colors ${
              showFilters || priorityFilter !== "all" || statusFilter !== "all"
                ? "bg-teal/10 border-teal/30 text-teal"
                : "border-border text-muted-foreground hover:border-muted-foreground/50"
            }`}
          >
            <Filter className="w-3.5 h-3.5" /> Filters
          </button>
        </div>

        {showFilters && (
          <div className="flex gap-3 -mt-4">
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="w-[160px]"><SelectValue placeholder="Priority" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                <SelectItem value="CRITICAL">Critical</SelectItem>
                <SelectItem value="HIGH">High</SelectItem>
                <SelectItem value="LOW">Moderate</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in-progress">In Progress</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
            {(priorityFilter !== "all" || statusFilter !== "all") && (
              <button onClick={() => { setPriorityFilter("all"); setStatusFilter("all"); }} className="text-xs text-teal underline hover:no-underline self-center">Clear</button>
            )}
          </div>
        )}

        {/* ── Lessons by Category (Sectioned Layout) ── */}
        {CATEGORIES.map(cat => {
          const catLessons = filtered.filter((l: any) => l.category === cat.key);
          if (catLessons.length === 0) return null;

          return (
            <section key={cat.key}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${cat.accent}15` }}>
                  <cat.icon className="w-4.5 h-4.5" style={{ color: cat.accent }} />
                </div>
                <div>
                  <h2 className="text-base font-bold text-foreground">{cat.label}</h2>
                  <p className="text-[10px] text-muted-foreground">{catLessons.length} item{catLessons.length !== 1 ? "s" : ""}</p>
                </div>
              </div>

              <div className="space-y-2 mb-8">
                {catLessons.map((lesson: any) => {
                  const expanded = expandedIds.has(lesson.id);
                  const pCfg = priorityBadge[lesson.priority as Priority] || priorityBadge.HIGH;
                  const sCfg = statusBadge[lesson.status] || statusBadge.open;
                  return (
                    <div
                      key={lesson.id}
                      className={`rounded-xl border bg-card transition-all duration-200 ${
                        expanded ? "border-teal/25 shadow-md" : "border-border hover:border-muted-foreground/30"
                      }`}
                    >
                      <button onClick={() => toggleExpand(lesson.id)} className="w-full text-left px-5 py-3.5 flex items-center gap-4">
                        <span
                          className="text-[11px] font-bold tracking-wide px-2 py-0.5 rounded border flex-shrink-0"
                          style={{ borderColor: cat.accent + "40", color: cat.accent, backgroundColor: cat.accent + "10" }}
                        >
                          {lesson.id}
                        </span>
                        <span className="flex-1 text-sm font-medium text-foreground leading-snug line-clamp-2">{lesson.title}</span>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${pCfg.cls}`}>{pCfg.label}</span>
                          <div className="flex items-center gap-1">
                            <div className={`w-2 h-2 rounded-full ${sCfg.dot}`} />
                            <span className="text-[10px] text-muted-foreground">{sCfg.label}</span>
                          </div>
                          {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                        </div>
                      </button>

                      {expanded && (
                        <div className="px-5 pb-5 border-t border-border">
                          <div className="grid md:grid-cols-2 gap-5 pt-4">
                            <div className="rounded-lg border border-red-100 bg-red-50/50 dark:bg-red-900/10 dark:border-red-900/30 p-4">
                              <p className="text-[10px] font-bold tracking-[1.5px] uppercase text-red-600/70 mb-2 flex items-center gap-1.5">
                                <AlertTriangle className="w-3 h-3" /> Root Cause — Angola
                              </p>
                              <p className="text-[13px] text-foreground/75 leading-relaxed">{lesson.root_cause}</p>
                            </div>
                            <div className="rounded-lg border border-emerald-100 bg-emerald-50/50 dark:bg-emerald-900/10 dark:border-emerald-900/30 p-4">
                              <p className="text-[10px] font-bold tracking-[1.5px] uppercase text-emerald-700/70 mb-2 flex items-center gap-1.5">
                                <CheckCircle2 className="w-3 h-3" /> Corrective Action — Guyana
                              </p>
                              <p className="text-[13px] text-foreground/75 leading-relaxed">{lesson.corrective_action}</p>
                            </div>
                          </div>

                          {lesson.status === "closed" && lesson.closed_by_name && (
                            <div className="mt-4 p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 flex items-center gap-2">
                              <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                              <div>
                                <p className="text-xs font-medium text-emerald-700 dark:text-emerald-400">
                                  Closed by {lesson.closed_by_name} on {new Date(lesson.closed_at).toLocaleDateString()}
                                </p>
                                {lesson.closure_notes && <p className="text-xs text-emerald-600/70 mt-0.5">{lesson.closure_notes}</p>}
                              </div>
                            </div>
                          )}

                          {/* Management Actions */}
                          {canManage && (
                            <div className="mt-4 flex gap-2">
                              {lesson.status !== "closed" ? (
                                <Button size="sm" variant="default" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setCloseDialog(lesson.id)}>
                                  <CheckCircle2 className="w-3 h-3 mr-1" /> Close Out Item
                                </Button>
                              ) : (
                                <Button size="sm" variant="outline" onClick={() => reopenMutation.mutate(lesson.id)}>
                                  Reopen Item
                                </Button>
                              )}
                            </div>
                          )}

                          <div className="mt-4 pt-4 border-t border-border">
                            <LessonCommentThread lessonId={lesson.id} variant="intranet" />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}

        {filtered.length === 0 && (
          <div className="text-center py-16 text-muted-foreground">
            <BookOpen className="w-8 h-8 mx-auto mb-3 opacity-30" />
            <p className="text-sm font-medium">No lessons match your current filters.</p>
            <button onClick={() => { setPriorityFilter("all"); setStatusFilter("all"); setSearch(""); }} className="text-xs text-teal underline mt-2">Reset all filters</button>
          </div>
        )}
      </div>

      {/* Close-out Dialog */}
      <Dialog open={!!closeDialog} onOpenChange={() => { setCloseDialog(null); setClosureNotes(""); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Close Out: {closeDialog}</DialogTitle>
            <DialogDescription>This will mark the item as closed and notify the client.</DialogDescription>
          </DialogHeader>
          <Textarea placeholder="Closure notes (optional)..." value={closureNotes} onChange={e => setClosureNotes(e.target.value)} rows={4} />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCloseDialog(null); setClosureNotes(""); }}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => closeDialog && closeoutMutation.mutate({ id: closeDialog, notes: closureNotes })} disabled={closeoutMutation.isPending}>
              {closeoutMutation.isPending ? "Closing..." : "Confirm Close"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </IntranetLayout>
  );
};

export default EmployeeLessonsLearned;
